import React, { useEffect, useMemo, ReactNode } from 'react';

// material-ui
import {
  createTheme,
  ThemeOptions,
  ThemeProvider,
  Theme,
  TypographyVariantsOptions,
} from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';

// project imports
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';

import componentStyleOverrides from './compStyleOverride';
import customShadows from './shadows';
import { getGlobalScrollbarStyles } from './globalScrollbarStyles';

// types
import { CustomShadowProps } from 'types/default-theme';

interface Props {
  children: ReactNode;
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    midxl: true;
    xl: true;
  }
}

export default function ThemeCustomization({ children }: Props) {
  const { borderRadius, fontFamily, mode, outlinedFilled, presetColor, themeDirection } = useConfig();

  const theme: Theme = useMemo<Theme>(() => Palette(mode, presetColor), [mode, presetColor]);

  const themeTypography: TypographyVariantsOptions = useMemo<TypographyVariantsOptions>(
    () => Typography(theme, borderRadius, fontFamily),
    [theme, borderRadius, fontFamily],
  );
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(
    () => customShadows(mode, theme),
    [mode, theme],
  );

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      direction: themeDirection,
      palette: theme.palette,
      mixins: {
        toolbar: {
          minHeight: '48px',
          padding: '16px',
        },
      },
      typography: themeTypography,
      customShadows: themeCustomShadows,
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          midxl: 1440,
          xl: 1536,
        },
      },
    }),
    [themeDirection, theme, themeCustomShadows, themeTypography],
  );

  const themes: Theme = useMemo(() => {
    const baseTheme = createTheme(themeOptions);
    return createTheme(baseTheme, {
      components: componentStyleOverrides(baseTheme, borderRadius, outlinedFilled) as Theme['components'],
    });
  }, [themeDirection, theme, themeCustomShadows, themeTypography, borderRadius, outlinedFilled]);

  useEffect(() => {
    document.documentElement.dataset.theme = themes.palette.mode;
  }, [themes.palette.mode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <CssBaseline enableColorScheme />
          <GlobalStyles styles={(theme) => getGlobalScrollbarStyles(theme)} />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
